import { useEffect, useState } from "react";
import { ExternalLink, FileText, Loader2, X } from "lucide-react";
import apiClient from "@/api/client";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api/v1";

interface Props {
  docId: string;
  page: number;
  /** "public": pakai endpoint publik tanpa auth. "admin": fetch blob + Bearer. */
  variant: "public" | "admin";
  onClose: () => void;
}

/**
 * Modal pratinjau dokumen sumber dari sebuah sitasi. Menampilkan file (PDF) di
 * dalam iframe yang melompat ke halaman tersitir (`#page=N`).
 *
 * - admin: file diambil sebagai blob via axios (Authorization Bearer otomatis dari
 *   interceptor), lalu di-render sebagai object URL — sama seperti DocumentDetailPage.
 * - public: iframe langsung menunjuk endpoint publik (tanpa auth).
 */
export default function CitationPreviewModal({ docId, page, variant, onClose }: Props) {
  const publicUrl = `${API_BASE}/public/dokumen/${docId}/preview`;
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(variant === "admin");
  const [error, setError] = useState<string | null>(null);
  const [notPdf, setNotPdf] = useState(false);

  // Tutup dengan Escape.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Admin: ambil file sebagai blob (auth) → object URL.
  useEffect(() => {
    if (variant !== "admin") return;
    let revoked: string | null = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotPdf(false);
    apiClient
      .get(`/documents/${docId}/preview`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        const type = (res.headers["content-type"] as string) || "";
        const blob = new Blob([res.data], { type });
        const url = window.URL.createObjectURL(blob);
        revoked = url;
        setBlobUrl(url);
        setNotPdf(!type.includes("pdf"));
      })
      .catch(() => {
        if (!cancelled) setError("Gagal memuat dokumen. Anda mungkin tidak punya akses, atau file tidak ditemukan.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (revoked) window.URL.revokeObjectURL(revoked);
    };
  }, [docId, variant]);

  const iframeSrc =
    variant === "admin"
      ? blobUrl
        ? `${blobUrl}#page=${page}`
        : null
      : `${publicUrl}#page=${page}`;

  // Tautan "buka di tab baru".
  const openHref = variant === "admin" ? blobUrl ?? undefined : publicUrl;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="fixed inset-0 bg-black/50" />

      <div className="relative flex h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2 text-gray-800">
            <FileText className="h-4 w-4 text-blue-600" />
            <h3 className="text-sm font-semibold">Sumber sitasi — Halaman {page}</h3>
          </div>
          <div className="flex items-center gap-1.5">
            {openHref && (
              <a
                href={openHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Buka dokumen
              </a>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              title="Tutup"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="relative flex-1 bg-gray-50">
          {loading && (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Memuat dokumen…
            </div>
          )}
          {!loading && error && (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-red-600">
              {error}
            </div>
          )}
          {!loading && !error && notPdf && (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <FileText className="h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-600">
                Pratinjau halaman hanya tersedia untuk PDF. Buka dokumen untuk melihat isinya.
              </p>
              {openHref && (
                <a
                  href={openHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  <ExternalLink className="h-4 w-4" />
                  Buka dokumen
                </a>
              )}
            </div>
          )}
          {!loading && !error && !notPdf && iframeSrc && (
            <iframe
              key={iframeSrc}
              src={iframeSrc}
              title={`Pratinjau dokumen halaman ${page}`}
              className="h-full w-full"
            />
          )}
        </div>
      </div>
    </div>
  );
}
